import React, { useEffect } from "react";
import CustomerSelector, { CustomerSelectorBaseProps } from "./CustomerSelector";
import { useLocalStore, useObserver } from "mobx-react";
import { CustomerCardFragment } from "@graphql";

export interface CustomerSelectorDropinStore {
  open: (options: OpenOptions) => void;
  select: (options: OpenOptions) => Promise<CustomerCardFragment | null>;
}

type Props = {
  storeRef?: (localStore: CustomerSelectorDropinStore) => void;
};

type OpenOptions = {
  selectorProps?: Partial<CustomerSelectorBaseProps>;
  onCancel?: () => void;
  onSelect?: (value: CustomerCardFragment) => void;
};

export function CustomerSelectorDropin(props: Props) {
  const localStore = useLocalStore(() => ({
    isVisible: false,
    options: null as OpenOptions,

    open(options: OpenOptions) {
      this.isVisible = true;
      this.options = options;
    },

    close() {
      this.isVisible = false;
      this.options = null;
    },

    select(options: OpenOptions) {
      return new Promise<CustomerCardFragment | null>((resolve) => {
        this.open({
          ...options,
          onCancel: () => {
            this.close();
            resolve(null);
          },
          onSelect: resolve
        });
      });
    },

    handleChange(value: Partial<CustomerCardFragment> | null) {
      this.options?.onSelect?.(value);
    },

    handleClose() {
      this.options?.onCancel();
    }
  }));

  useEffect(() => {
    props.storeRef?.(localStore);
  }, [props.storeRef]);

  return useObserver(() => {
    if (!localStore.isVisible) {
      return null;
    }

    return (
      <CustomerSelector
        open
        slim
        multi={false}
        renderControl={() => null}
        onChange={localStore.handleChange}
        onSearchClose={localStore.handleClose}
        value={null}
        {...localStore.options.selectorProps}
      />
    );
  });
}

export class CustomerSelectorDropinRef {
  private ref: CustomerSelectorDropinStore;

  register = (ref: CustomerSelectorDropinStore) => {
    this.ref = ref;
  };

  get current() {
    return this.ref;
  }
}
